import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField, Typography, Box } from '@mui/material';
import axios from 'axios';

/** 한 글자로는 의미 있는 결과가 없고 호출만 늘어난다. */
const MIN_LENGTH = 2;
/** 타이핑이 멈춘 뒤에만 부른다. 글자마다 부르면 카카오 호출이 몇 배로 늘어난다. */
const DEBOUNCE_MS = 350;

/** 자동완성에서 고른 결과를 입력칸에 넣을 문자열로 만든다. 장소명이 있으면 주소와 함께 남긴다. */
function toInputValue(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  if (item.name) return `${item.name} (${item.address})`;
  return item.address;
}

/**
 * 출발지·도착지 입력칸. 장소명("인천공항")과 주소("송파구 중대로 192") 모두 검색된다.
 *
 * 서버(/place/search)가 카카오를 대신 호출하므로 브라우저에 키가 노출되지 않는다.
 * 검색이 실패하거나 키가 없으면 목록만 안 뜨고 자유입력으로 그대로 쓸 수 있다.
 */
export default function PlaceAutocomplete({
  label,
  name,
  value,
  onChange,
  error,
  helperText,
  required,
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  // 폼이 초기화되거나 외부에서 값이 바뀌면 따라간다
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    const keyword = inputValue.trim();
    if (keyword.length < MIN_LENGTH) {
      setOptions([]);
      return undefined;
    }

    const timer = setTimeout(() => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setLoading(true);

      axios
        .get('/place/search', { params: { query: keyword } })
        .then(({ data }) => {
          // 늦게 도착한 옛 응답이 최신 결과를 덮어쓰지 않게 한다
          if (requestIdRef.current !== requestId) return;
          setOptions(data?.items || []);
        })
        .catch(() => {
          if (requestIdRef.current !== requestId) return;
          setOptions([]); // 검색이 안 되어도 자유입력은 계속 가능해야 한다
        })
        .finally(() => {
          if (requestIdRef.current === requestId) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const noOptionsText = useMemo(
    () => (inputValue.trim().length < MIN_LENGTH ? '두 글자 이상 입력하세요' : '검색 결과가 없습니다'),
    [inputValue],
  );

  return (
    <Autocomplete
      freeSolo
      autoComplete
      filterOptions={(x) => x} // 서버가 이미 걸러 준 결과를 그대로 보여준다
      options={options}
      loading={loading}
      loadingText='찾는 중…'
      noOptionsText={noOptionsText}
      inputValue={inputValue}
      getOptionLabel={toInputValue}
      isOptionEqualToValue={(option, selected) => toInputValue(option) === toInputValue(selected)}
      onInputChange={(event, next, reason) => {
        if (reason === 'reset' && !next) return;
        setInputValue(next);
        onChange(next);
      }}
      onChange={(event, selected) => {
        const text = toInputValue(selected);
        setInputValue(text);
        onChange(text);
      }}
      renderOption={(props, option) => (
        <Box component='li' {...props} key={`${option.name}${option.address}`}>
          <Box>
            {option.name && <Typography sx={{ fontSize: 14 }}>{option.name}</Typography>}
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{option.address}</Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          name={name}
          size='small'
          fullWidth
          required={required}
          error={Boolean(error)}
          helperText={helperText || '장소명·주소로 검색하거나 직접 입력하세요'}
          autoComplete='off'
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
